import React, { useState, useEffect, useCallback } from "react";
import { UserPlus, Mail, Check, X, Users, Search } from "lucide-react";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db, auth } from "../../../Services/firebase-config";
import { useAuth } from "../../../Contexts/AuthContext";
import TeamMemberCard from "./TeamMemberCard";
import TeamChat from "./Chat";
import ViewAvailableTeams from "./viewAvailableTeams";
import TeamJoinRequests from "./teamJoinRequests";
import {
  trackHackathonEvent,
  HACKATHON_EVENTS,
} from "../../../Services/analytics";

const Teams = ({
  eventId,
  currentTeam,
  userApplication,
  setCurrentTeam,
  teamMembers,
  isTeamLead,
  onTeamUpdate,
}) => {
  const { userData } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAvailableTeams, setShowAvailableTeams] = useState(false);

  // Fetch all users for team invitations
  const fetchUsers = useCallback(async () => {
    try {
      // This function is currently not used but kept for future features
      // const usersQuery = query(collection(db, 'users'));
      // const usersSnapshot = await getDocs(usersQuery);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  // Fetch invitations for current user
  const fetchInvitations = useCallback(async () => {
    if (!auth.currentUser || !eventId) return;

    try {
      console.log("Fetching Invitations");
      const invitationsQuery = query(
        collection(db, "teamInvitations"),
        where("inviteeEmail", "==", auth.currentUser.email),
        where("eventId", "==", eventId),
        where("status", "==", "pending")
      );

      const invitationsSnapshot = await getDocs(invitationsQuery);
      const invitationsData = invitationsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvitations(invitationsData);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  }, [eventId]);

  useEffect(() => {
    fetchInvitations();
    fetchUsers();
    // Set team name and description from props
    if (currentTeam) {
      setTeamName(currentTeam.name || "");
      setTeamDescription(currentTeam.description || "");
    }
  }, [fetchInvitations, fetchUsers, currentTeam]);

  // Create a new team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      // Check if user is already in a team for this event using the prop
      if (currentTeam) {
        setError("You are already part of a team for this event.");
        setIsLoading(false);
        return;
      } // Create user data object for team member
      const currentUserData = {
        id: auth.currentUser.uid,
        name:
          userData?.displayName ||
          userData?.name ||
          auth.currentUser.displayName,
        email: userData?.email || auth.currentUser.email,
        phoneNumber: userData?.phoneNumber || "",
        photoURL: userData?.photoURL || auth.currentUser.photoURL || "",
        skills: userData?.skills || [],
      };

      const teamData = {
        name: teamName,
        description: teamDescription,
        eventId: eventId,
        hasApplied: false,
        memberIds: [auth.currentUser.uid],
        members: [currentUserData],
        createdBy: auth.currentUser.uid,
        createdAt: new Date(),
        maxMembers: 5,
      };

      await addDoc(collection(db, "hackathonTeams"), teamData);

      // Track successful team creation
      trackHackathonEvent(HACKATHON_EVENTS.TEAM_CREATED, {
        team_name: teamName,
        event_id: eventId,
        creator_id: auth.current?.uid,
      });

      setShowCreateTeam(false);
      setTeamName("");
      setTeamDescription("");
      setCurrentTeam(teamData);
      onTeamUpdate(); // Refresh team data
    } catch (error) {
      setError("Error creating team: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Send team invitation
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      // Check if team is full
      if (currentTeam.members.length >= 5) {
        setError("Team is already full (5 members maximum).");
        setIsLoading(false);
        return;
      }

      // Check if user exists
      const userQuery = query(
        collection(db, "users"),
        where("email", "==", inviteEmail)
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setError("User with this email does not exist.");
        setIsLoading(false);
        return;
      }

      // Check if user is already in a team
      const existingTeamQuery = query(
        collection(db, "hackathonTeams"),
        where("eventId", "==", eventId),
        where("members", "array-contains", userSnapshot.docs[0].id)
      );

      const existingTeamSnapshot = await getDocs(existingTeamQuery);

      if (!existingTeamSnapshot.empty) {
        setError("This user is already part of a team for this event.");
        setIsLoading(false);
        return;
      }

      // Check if invitation already exists
      const existingInvitationQuery = query(
        collection(db, "teamInvitations"),
        where("inviteeEmail", "==", inviteEmail),
        where("teamId", "==", currentTeam.id),
        where("status", "==", "pending")
      );

      const existingInvitationSnapshot = await getDocs(existingInvitationQuery);

      if (!existingInvitationSnapshot.empty) {
        setError("An invitation has already been sent to this user.");
        setIsLoading(false);
        return;
      }

      const invitationData = {
        teamId: currentTeam.id,
        teamName: currentTeam.name,
        eventId: eventId,
        inviterEmail: auth.currentUser.email,
        inviterName: auth.currentUser.displayName,
        inviteeEmail: inviteEmail,
        status: "pending",
        createdAt: new Date(),
      };

      // Get the invited user's ID
      const invitedUserId = userSnapshot.docs[0].id;

      // Create notification data
      const notificationData = {
        userId: invitedUserId,
        message: `${auth.currentUser.displayName || "Someone"
          } invited you to join team "${currentTeam.name}"`,
        read: false,
        timestamp: new Date(),
        type: "team_invitation",
        relatedData: {
          teamId: currentTeam.id,
          teamName: currentTeam.name,
          eventId: eventId,
        },
      };

      // Use Promise.all to create both invitation and notification simultaneously
      await Promise.all([
        addDoc(collection(db, "teamInvitations"), invitationData),
        addDoc(collection(db, "notifications"), notificationData),
      ]);

      setInviteEmail("");
      setSuccessMessage("Invitation sent successfully!");
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      setError("Error sending invitation: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Accept team invitation
  const handleAcceptInvitation = async (invitationId, teamId) => {
    setIsLoading(true);
    setError("");

    try {
      // Create user data object for team member
      const currentUserData = {
        id: auth.currentUser.uid,
        name:
          userData?.displayName ||
          userData?.name ||
          auth.currentUser.displayName,
        email: userData?.email || auth.currentUser.email,
        phoneNumber: userData?.phoneNumber || "",
        photoURL: userData?.photoURL || auth.currentUser.photoURL || "",
        skills: userData?.skills || [],
      };

      // Add user to team
      const teamRef = doc(db, "hackathonTeams", teamId);
      await updateDoc(teamRef, {
        memberIds: arrayUnion(auth.currentUser.uid),
        members: arrayUnion(currentUserData),
      });

      // Update invitation status
      const invitationRef = doc(db, "teamInvitations", invitationId);
      await updateDoc(invitationRef, {
        status: "accepted",
        acceptedAt: new Date(),
      });

      // Refresh data
      onTeamUpdate(); // Refresh team data
      await fetchInvitations();
    } catch (error) {
      setError("Error accepting invitation: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Decline team invitation
  const handleDeclineInvitation = async (invitationId) => {
    setIsLoading(true);
    setError("");

    try {
      const invitationRef = doc(db, "teamInvitations", invitationId);
      await updateDoc(invitationRef, {
        status: "declined",
        declinedAt: new Date(),
      });

      await fetchInvitations();
    } catch (error) {
      setError("Error declining invitation: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Update team information
  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const teamRef = doc(db, "hackathonTeams", currentTeam.id);
      await updateDoc(teamRef, {
        name: teamName,
        description: teamDescription,
        updatedAt: new Date(),
      });

      onTeamUpdate(); // Refresh team data
      setSuccessMessage("Team updated successfully!");
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      setError("Error updating team: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Leave team
  const handleLeaveTeam = async () => {
    setIsLoading(true);
    setError("");

    try {
      const teamRef = doc(db, "hackathonTeams", currentTeam.id);

      if (isTeamLead) {
        // If team leader is leaving, delete the team
        await deleteDoc(teamRef);

        // Delete all pending invitations for this team
        const invitationsQuery = query(
          collection(db, "teamInvitations"),
          where("teamId", "==", currentTeam.id),
          where("status", "==", "pending")
        );
        const invitationsSnapshot = await getDocs(invitationsQuery);

        for (const invitationDoc of invitationsSnapshot.docs) {
          await deleteDoc(doc(db, "teamInvitations", invitationDoc.id));
        }
      } else {
        // Remove user from team - find the user's object in the members array
        const currentUserMember = currentTeam.members.find((member) => {
          if (typeof member === "string") {
            return member === auth.currentUser.uid;
          } else {
            return (member.id || member.userId) === auth.currentUser.uid;
          }
        });

        if (currentUserMember) {
          await updateDoc(teamRef, {
            memberIds: arrayRemove(auth.currentUser.uid),
            members: arrayRemove(currentUserMember),
          });
        }
      }

      setCurrentTeam(null); // Immediately clear current team
      onTeamUpdate(); // Refresh team data
      setTeamName("");
      setTeamDescription("");
    } catch (error) {
      setError("Error leaving team: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Show Available Teams view
  if (showAvailableTeams) {
    return (
      <ViewAvailableTeams
        eventId={eventId}
        onBack={() => setShowAvailableTeams(false)}
        currentUserTeam={currentTeam}
      />
    );
  }

  if (showChat && currentTeam) {
    return (
      <TeamChat teamId={currentTeam.id} teamData={currentTeam} onBack={() => setShowChat(false)} />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-3 sm:px-4 py-2 sm:py-3 rounded-md shadow-lg max-w-xs sm:max-w-md">
          <div className="flex items-center">
            <Check className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="text-xs sm:text-sm font-medium">
              {successMessage}
            </span>
            <button
              onClick={() => setSuccessMessage("")}
              className="ml-2 sm:ml-4 text-green-700 hover:text-green-900 transition-colors"
            >
              <X className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-100 text-red-800 rounded-lg text-sm sm:text-base">
          {error}
        </div>
      )}

      {!currentTeam ? (
        // No team - show create team or invitations
        <div className="space-y-4 sm:space-y-8">
          {/* Create Team Section */}
          <div className="mb-4 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-6 gap-3">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                Team Management
              </h2>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {!showCreateTeam && (
                  <>
                    <button
                      onClick={() => setShowCreateTeam(true)}
                      className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 text-sm sm:text-base"
                    >
                      <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                      Create Team
                    </button>
                    <button
                      onClick={() => setShowAvailableTeams(true)}
                      className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-custom-green-1 text-white rounded-lg hover:bg-green-600 text-sm sm:text-base"
                    >
                      <Search className="w-3 h-3 sm:w-4 sm:h-4" />
                      View Available Teams
                    </button>
                  </>
                )}
              </div>
            </div>

            {showCreateTeam && (
              <div className="border rounded-lg p-3 sm:p-6 bg-gray-50">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                  Create Your Team
                </h3>
                <form
                  onSubmit={handleCreateTeam}
                  className="space-y-3 sm:space-y-4"
                >
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Team Name
                    </label>
                    <input
                      type="text"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Team Description
                    </label>
                    <textarea
                      rows={3}
                      value={teamDescription}
                      onChange={(e) => setTeamDescription(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div className="flex gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 sm:px-6 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {isLoading ? "Creating..." : "Create Team"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateTeam(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!showCreateTeam && (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>You're not part of any team yet.</p>
                <p>Create a team or browse available teams to join one.</p>
              </div>
            )}
          </div>

          {/* Invitations Section */}
          {invitations.length > 0 && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-6">Team Invitations</h3>
              <div className="space-y-4">
                {invitations.map(invitation => (
                  <div key={invitation.id} className="border rounded-lg p-4 bg-blue-50">
                    {/* Mobile First Layout */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Team Info Section */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-custom-blue-1 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-800 truncate">{invitation.teamName}</h4>
                          <p className="text-sm text-gray-600 truncate">
                            Invited by {invitation.inviterName}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons Section */}
                      <div className="flex gap-2 sm:flex-shrink-0">
                        <button
                          onClick={() => handleAcceptInvitation(invitation.id, invitation.teamId)}
                          disabled={isLoading}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-custom-green-1 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm font-medium flex-1 sm:flex-initial min-w-0"
                        >
                          <Check className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">Accept</span>
                        </button>
                        <button
                          onClick={() => handleDeclineInvitation(invitation.id)}
                          disabled={isLoading}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-sm font-medium flex-1 sm:flex-initial min-w-0"
                        >
                          <X className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">Decline</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Has team - show team management
        <div className="space-y-8">
          {/* Team Overview */}
          <div className="mb-8">
            {/* Team Chat Button */}
            {currentTeam && (
              <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  className="px-4 py-2 bg-custom-green-1 text-white rounded hover:bg-green-600 text-sm sm:text-base"
                  onClick={() => setShowChat(true)}
                >
                  Open Team Chat
                </button>
                {((!isTeamLead) || (isTeamLead && currentTeam && currentTeam.members && currentTeam.members.length === 1)) && (
                  <button
                    onClick={() => setShowAvailableTeams(true)}
                    className="px-4 py-2 bg-custom-blue-1 text-white rounded hover:bg-custom-blue-1/90 text-sm sm:text-base"
                  >
                    View Available Teams
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                My Team
              </h2>
              <div className="flex flex-col sm:flex-row gap-2">
                {isTeamLead &&
                  currentTeam.members.length < 5 &&
                  !userApplication && (
                    <form
                      onSubmit={handleSendInvitation}
                      className="flex flex-col sm:flex-row gap-2"
                    >
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="Enter email to invite"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent text-sm sm:text-base"
                        required
                      />
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 disabled:opacity-50 text-sm sm:text-base"
                      >
                        <UserPlus className="w-3 h-3 sm:w-4 sm:h-4" />
                        Invite
                      </button>
                    </form>
                  )}
                {!userApplication && (
                  <button
                    onClick={() => setShowLeaveConfirm(true)}
                    className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm sm:text-base"
                  >
                    <span className="hidden sm:inline">Leave Team</span>
                    <span className="sm:hidden">Leave</span>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {teamMembers.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  isOwner={member.isOwner}
                />
              ))}

              {/* Empty slots for remaining members */}
              {Array.from({ length: 5 - teamMembers.length }, (_, index) => (
                <div
                  key={`empty-${index}`}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 flex flex-col items-center justify-center text-gray-400"
                >
                  <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 mb-2" />
                  <span className="text-xs sm:text-sm">Empty Slot</span>
                </div>
              ))}
            </div>

            {/* Warning message for teams with less than 3 members */}
            {teamMembers.length < 3 && !currentTeam.mentorId && (
              <div className="mt-4 sm:mt-6 border-2 border-dashed border-orange-300 rounded-lg p-4 sm:p-6 bg-orange-50">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="text-center sm:text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-orange-800 mb-1">Team Size Warning</h4>
                    <p className="text-sm sm:text-base text-orange-700">
                      Your team needs at least 3 members to be assigned a mentor. Please add {3 - teamMembers.length} more {3 - teamMembers.length === 1 ? 'member' : 'members'} to qualify for mentor assignment.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Join Requests Section - Only for team leaders */}
          {isTeamLead && (
            <div className="border-t border-gray-200 pt-8">
              <TeamJoinRequests
                currentTeam={currentTeam}
                eventId={eventId}
                onTeamUpdate={onTeamUpdate}
              />
            </div>
          )}

          {/* Team Settings - Only for team leader */}
          {isTeamLead && (
            <>
              <div className="border-t border-gray-300 my-8"></div>
              <div className="mb-8 sm:mb-12">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6">
                  Team Settings
                </h3>
                <form
                  onSubmit={handleUpdateTeam}
                  className="space-y-4 sm:space-y-6"
                >
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Team Name
                    </label>
                    <input
                      type="text"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Team Description
                    </label>
                    <textarea
                      rows={3}
                      value={teamDescription}
                      onChange={(e) => setTeamDescription(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div className="flex gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 sm:px-6 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 disabled:opacity-50 text-sm sm:text-base"
                    >
                      {isLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      )}

      {/* Leave Team Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-8 max-w-md mx-4 text-center shadow-2xl w-full">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <X className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
            </div>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Leave Team
            </h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
              Are you sure you want to leave this team? This action cannot be
              undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 py-2 sm:py-3 px-4 sm:px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLeaveConfirm(false);
                  handleLeaveTeam();
                }}
                disabled={isLoading}
                className="flex-1 py-2 sm:py-3 px-4 sm:px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                {isLoading ? "Leaving..." : "Leave Team"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;